import {
    CollectionEstimate,
    CollectionInvoice,
} from '../../../providers/CollectionBillingProvider';
import type { InvoiceStatus } from '../components/detail/InvoiceItem/InvoiceItem';

const useBillingRender = () => {
    const isInvoicePaid = (invoice: CollectionInvoice): boolean =>
        invoice.paye || invoice.totalTtc <= 0 || invoice.montantPaiementRecu >= invoice.totalTtc;

    const statusFromInvoice = (invoice: CollectionInvoice): InvoiceStatus => {
        if (invoice.factureEnPreparation) {
            return 'draft';
        }
        if (isInvoicePaid(invoice)) {
            return 'paid';
        }

        return 'pending';
    };

    const getInvoiceLabel = (invoice: CollectionInvoice): string => {
        const totalTtc = invoice.totalTtc as unknown as number;

        if (totalTtc < 0) {
            return `Avoir ${invoice.numero}`;
        }

        return `Facture ${invoice.numero}`;
    };

    const feedbackFromInvoice = (invoice: CollectionInvoice): string => {
        if (invoice.factureEnPreparation) {
            return 'En préparation';
        }
        if (isInvoicePaid(invoice)) {
            return 'Réglé';
        }

        return 'À régler';
    };

    const getInvoiceRemainingAmount = (invoice: CollectionInvoice): number => {
        const totalTtc = invoice.totalTtc as unknown as number;
        const alreadyPaid = invoice.montantPaiementRecu as unknown as number;

        if (totalTtc < 0) {
            return 0;
        }

        return totalTtc - alreadyPaid;
    };

    const getTotalRemainingAmount = (invoices: CollectionInvoice[]): number =>
        invoices
            .filter((item) => item.paye === false && item.factureEnPreparation === false)
            .reduce((prev, current) => prev + getInvoiceRemainingAmount(current), 0);

    const getEstimateLabel = (estimate: CollectionEstimate): string => `Devis ${estimate.numero}`;

    const hasActiveInvoice = (invoices: CollectionInvoice[]): boolean =>
        invoices.find(
            (item) =>
                item.totalTtc > 0 &&
                !item.factureAvoir &&
                item.deleted === false &&
                !item.idFactureRefAvoir,
        ) !== undefined;

    const canUpdateBillingInfos = (invoices: CollectionInvoice[]): boolean =>
        !hasActiveInvoice(invoices);

    return {
        statusFromInvoice,
        getInvoiceLabel,
        feedbackFromInvoice,
        getInvoiceRemainingAmount,
        getEstimateLabel,
        getTotalRemainingAmount,
        canUpdateBillingInfos,
        hasActiveInvoice,
    };
};

export default useBillingRender;
